import { MenuItem } from "@kaltura/ds-react-components";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { useIsInsidePwa } from "../useIsInsidePwa";
import { usePwaStore } from "../usePwaStore";
import { isDesktop } from "react-device-detect";
import { QrCodeModal } from "../qr-code-modal/QrCodeModal";
import { useState } from "react";
import { InstructionsDrawerContainer, supportsInstructions } from "../instructions/InstructionsDrawerContainer";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

interface MobileAppMenuItemProps {
    appName: string;
    appIconUrl: string;
    closeMenu: () => void;
}

/**
 * Button linking to the mobile app.
 */
export const MobileAppMenuItem = (props: MobileAppMenuItemProps) => (
    <ButtonAnalyticsProvider applicationFeature={"webapp"}>
        <MobileAppMenuItemContent {...props} />
    </ButtonAnalyticsProvider>
);

const MobileAppMenuItemContent = ({ closeMenu, appName, appIconUrl, ...rest }: MobileAppMenuItemProps) => {
    const isInsidePwa = useIsInsidePwa();
    const { canInstall, install, dismissed } = usePwaStore();
    const sendButtonAnalytics = useButtonAnalytics();

    const [showQrCode, setShowQrCode] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);

    if (isInsidePwa) {
        return null;
    }

    let handleClick: () => void | Promise<void>;
    if (isDesktop) {
        // On desktop, the "mobile app" button is always present, and leads to the QR code
        handleClick = () => {
            sendButtonAnalytics("Header the mobile app cta", ButtonClickAnalyticsType.OPEN);
            setShowQrCode(true);
        };
    }
    else {
        // On mobile, the "mobile app" button shows only after dismissing the main banner
        if (!dismissed) {
            return null;
        }

        if (supportsInstructions) {
            // On iOS, we can't detect whether the app was installed or not, so we always show the installation instructions.
            handleClick = () => {
                sendButtonAnalytics("Header the mobile app cta", ButtonClickAnalyticsType.OPEN);
                setShowInstructions(true);
            };
        }
        else {
            // On android, the "mobile app" button shows only when the app can actually be installed.
            if (!canInstall) {
                return null;
            }

            handleClick = async () => {
                sendButtonAnalytics("Header the mobile app install", ButtonClickAnalyticsType.LAUNCH);
                closeMenu();
                const outcome = await install();
                if (outcome) {
                    sendButtonAnalytics(
                        outcome === "accepted" ? "Accept install prompt" : "Dismiss install prompt",
                        outcome === "accepted" ? ButtonClickAnalyticsType.LAUNCH : ButtonClickAnalyticsType.CLOSE
                    );
                }
            };
        }
    }

    return (
        <>
            <MenuItem {...rest} onClick={handleClick}>
                {translate("The mobile app")}
            </MenuItem>

            <QrCodeModal
                open={showQrCode}
                onClose={() => {
                    setShowQrCode(false);
                    closeMenu();
                }}
            />

            <InstructionsDrawerContainer
                open={showInstructions}
                onClose={() => {
                    setShowInstructions(false);
                    closeMenu();
                }}
                appName={appName}
                appIconUrl={appIconUrl}
            />
        </>
    );
};
